import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Clock3,
  FileText,
  MapPin,
  RefreshCw,
  Utensils,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DeliveryMap from "@/components/DeliveryMap";

const STEPS = [
  { key: "new", label: "Enquiry Submitted" },
  { key: "quotation_sent", label: "Quotation Sent" },
  { key: "confirmed", label: "Quotation Accepted" },
  { key: "advance_paid", label: "Advance Paid" },
  { key: "preparing", label: "Preparing" },
  { key: "ready", label: "Ready" },
  { key: "out_for_delivery", label: "Out for Delivery" },
  { key: "completed", label: "Completed" },
];

const STATUS_INDEX = Object.fromEntries(
  STEPS.map((step, index) => [step.key, index])
);

export default function CateringTracking() {
  const { enquiryId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // =========================================================
  // LOAD TRACKING
  // =========================================================

  const loadTracking = useCallback(
    async (silent = false) => {
      if (!enquiryId) return;

      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        const trackingResponse = await api.get(
          `/catering/${enquiryId}/tracking`
        );

        const trackingData = trackingResponse.data;

        setData(trackingData);

        // =====================================================
        // QUOTATION
        // =====================================================

        if (trackingData?.quotation) {
          setQuotation(trackingData.quotation);
        } else {
          setQuotation(null);

          try {
            const quotationResponse = await api.get(
              `/catering/${enquiryId}/quotation`
            );

            setQuotation(
              quotationResponse.data?.quotation || null
            );
          } catch (quotationError) {
            setQuotation(null);

            console.log(
              "Quotation not available yet:",
              quotationError?.response?.data?.detail ||
                quotationError?.message
            );
          }
        }
      } catch (error) {
        console.error(
          "Unable to load catering tracking:",
          error
        );

        if (!silent) {
          toast.error(
            error?.response?.data?.detail ||
              "Unable to load catering tracking."
          );
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [enquiryId]
  );

  // =========================================================
  // LOAD + AUTO REFRESH
  // =========================================================

  useEffect(() => {
    if (!enquiryId) return;

    loadTracking();

    const interval = setInterval(() => {
      loadTracking(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [enquiryId, loadTracking]);

  // =========================================================
  // CURRENT STATUS
  // =========================================================

  const currentIndex = useMemo(() => {
    if (!data) return 0;

    return STATUS_INDEX[data.status] ?? 0;
  }, [data]);

  // =========================================================
  // LOADING
  // =========================================================

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">

          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#B8E14A]" />

          <p className="mt-4 text-white/60">
            Loading catering tracking...
          </p>

        </div>
      </div>
    );
  }

  // =========================================================
  // NOT FOUND
  // =========================================================

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">

        <div className="glass rounded-3xl p-8 max-w-md w-full text-center">

          <h1 className="font-heading text-3xl">
            Catering enquiry not found
          </h1>

          <button
            type="button"
            onClick={() => navigate("/catering")}
            className="btn-primary w-full justify-center py-4 mt-6"
          >
            Back to Catering
          </button>

        </div>

      </div>
    );
  }

  // =========================================================
  // EVENT DESTINATION
  // =========================================================

  const destinationLat = Number(
    data?.latitude
  );

  const destinationLng = Number(
    data?.longitude
  );

  const hasDestination =
    Number.isFinite(destinationLat) &&
    Number.isFinite(destinationLng);

  // =========================================================
  // DELIVERY PARTNER LIVE LOCATION
  // =========================================================

  const liveLat = Number(
    data?.current_lat ??
      data?.delivery_partner_lat
  );

  const liveLng = Number(
    data?.current_lng ??
      data?.delivery_partner_lng
  );

  const hasLiveLocation =
    Number.isFinite(liveLat) &&
    Number.isFinite(liveLng);

  // =========================================================
  // DELIVERY STATUS
  // =========================================================

  const isOutForDelivery =
    data?.status === "out_for_delivery";

  const eventCompleted =
    data?.status === "completed";

  // =========================================================
  // QUOTATION
  // =========================================================

  const quotationAvailable =
    Boolean(quotation);

  const totalAmount = Number(
    quotation?.total_amount || 0
  );

  const advanceAmount = Number(
    quotation?.advance_amount || 0
  );

  const balanceAmount = Number(
    quotation?.balance_amount || 0
  );

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="min-h-screen">

      <section className="max-w-5xl mx-auto px-4 md:px-12 py-12 md:py-20">

        {/* =====================================================
            BACK BUTTON
        ===================================================== */}

        <button
          type="button"
          onClick={() => navigate("/catering")}
          className="flex items-center gap-2 text-sm text-white/60 hover:text-white mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Catering
        </button>

        <div className="glass rounded-[2rem] p-5 md:p-10">

          {/* ===================================================
              HEADER
          =================================================== */}

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">

            <div>

              <div className="text-[#B8E14A] text-xs uppercase tracking-widest">
                Catering Tracking
              </div>

              <h1 className="font-heading text-4xl md:text-5xl mt-2">
                {data.event_type || "Catering Event"}
              </h1>

              <p className="text-white/60 mt-2">
                {data.guests || 0} guests ·{" "}
                {data.event_date || "Date not set"}
                {data.event_time
                  ? ` · ${data.event_time}`
                  : ""}
              </p>

            </div>

            <button
              type="button"
              onClick={() => loadTracking(true)}
              disabled={refreshing}
              className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm flex items-center gap-2 disabled:opacity-50"
            >

              <RefreshCw
                className={`w-4 h-4 ${
                  refreshing ? "animate-spin" : ""
                }`}
              />

              Refresh

            </button>

          </div>

          {/* ===================================================
              TRACKING TIMELINE
          =================================================== */}

          <div className="mt-10">

            <div className="space-y-5">

              {STEPS.map((step, index) => {

                const completed =
                  index < currentIndex;

                const current =
                  index === currentIndex;

                const isLast =
                  index === STEPS.length - 1;

                return (
                  <div
                    key={step.key}
                    className="relative flex gap-4"
                  >

                    {!isLast && (
                      <div
                        className={`absolute left-[15px] top-8 w-px h-[calc(100%+8px)] ${
                          index < currentIndex
                            ? "bg-[#B8E14A]"
                            : "bg-white/10"
                        }`}
                      />
                    )}

                    <div
                      className={`relative z-10 w-8 h-8 shrink-0 rounded-full flex items-center justify-center border ${
                        completed || current
                          ? "bg-[#B8E14A] text-black border-[#B8E14A]"
                          : "bg-white/5 text-white/40 border-white/10"
                      }`}
                    >

                      {completed || current ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        <Clock3 className="w-4 h-4" />
                      )}

                    </div>

                    <div className="pb-1">

                      <div
                        className={`font-medium ${
                          current
                            ? "text-[#B8E14A]"
                            : ""
                        }`}
                      >
                        {step.label}
                      </div>

                      {current && (
                        <p className="text-sm text-white/50 mt-1">
                          Current status
                        </p>
                      )}

                    </div>

                  </div>
                );
              })}

            </div>

          </div>

          {/* ===================================================
              KITCHEN + EVENT LOCATION
          =================================================== */}

          <div className="grid md:grid-cols-2 gap-4 mt-10">

            {/* KITCHEN */}

            <div className="rounded-3xl bg-white/5 border border-white/10 p-5">

              <div className="flex items-center gap-3 mb-4">

                <div className="w-10 h-10 rounded-2xl bg-[#B8E14A]/15 flex items-center justify-center">

                  <Utensils className="w-5 h-5 text-[#B8E14A]" />

                </div>

                <div>

                  <div className="text-xs uppercase tracking-widest text-white/40">
                    Kitchen
                  </div>

                  <div className="font-heading text-xl">
                    {data.kitchen_name ||
                      "Not assigned yet"}
                  </div>

                </div>

              </div>

              {data.shop_name && (
                <p className="text-sm text-white/50">
                  {data.shop_name}
                </p>
              )}

              {!data.kitchen_name && (
                <p className="text-sm text-white/50">
                  Our admin team will assign a
                  kitchen after advance payment
                  is verified.
                </p>
              )}

            </div>

            {/* EVENT LOCATION */}

            <div className="rounded-3xl bg-white/5 border border-white/10 p-5">

              <div className="flex items-center gap-3 mb-4">

                <div className="w-10 h-10 rounded-2xl bg-[#FF69B4]/15 flex items-center justify-center">

                  <MapPin className="w-5 h-5 text-[#FF69B4]" />

                </div>

                <div>

                  <div className="text-xs uppercase tracking-widest text-white/40">
                    Event Location
                  </div>

                  <div className="font-medium">
                    {data.location || "-"}
                  </div>

                </div>

              </div>

              {data.pincode && (
                <p className="text-sm text-white/50">
                  Pincode: {data.pincode}
                </p>
              )}

              {data.map_link && (
                <a
                  href={data.map_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#B8E14A] mt-3"
                >
                  <MapPin className="w-4 h-4" />
                  Open location
                </a>
              )}

            </div>

          </div>

          {/* ===================================================
              LIVE CATERING DELIVERY MAP

              IMPORTANT:
              Map appears ONLY when:
              1. Status = out_for_delivery
              2. Delivery partner GPS coordinates exist
          =================================================== */}

          {isOutForDelivery &&
            hasLiveLocation && (
              <div className="mt-6 rounded-3xl bg-white/5 border border-white/10 p-5">

                {/* MAP HEADER */}

                <div className="flex items-center gap-3 mb-2">

                  <div className="w-10 h-10 rounded-2xl bg-[#B8E14A]/15 flex items-center justify-center">

                    <MapPin className="w-5 h-5 text-[#B8E14A]" />

                  </div>

                  <div>

                    <div className="text-xs uppercase tracking-widest text-white/40">
                      Live Delivery
                    </div>

                    <div className="font-heading text-xl">
                      Track Your Catering Delivery
                    </div>

                  </div>

                </div>

                {/* MAP DESCRIPTION */}

                <p className="text-sm text-white/50 mb-3">
                  Your delivery partner's live location is shown below.
                </p>

                {/* MAP */}

                <DeliveryMap
                  lat={liveLat}
                  lng={liveLng}
                  destinationLat={
                    hasDestination
                      ? destinationLat
                      : null
                  }
                  destinationLng={
                    hasDestination
                      ? destinationLng
                      : null
                  }
                />

                {/* AUTO UPDATE */}

                <p className="text-sm text-white/50 mt-3">
                  🚴 Delivery partner location updates automatically.
                </p>

              </div>
            )}

          {/* ===================================================
              GPS WAITING MESSAGE

              Shown after delivery starts but before the
              first GPS coordinate arrives.
          =================================================== */}

          {isOutForDelivery &&
            !hasLiveLocation && (
              <div className="mt-6 rounded-3xl bg-white/5 border border-white/10 p-5">

                <div className="flex items-center gap-3">

                  <div className="w-10 h-10 rounded-2xl bg-[#B8E14A]/15 flex items-center justify-center">

                    <MapPin className="w-5 h-5 text-[#B8E14A]" />

                  </div>

                  <div>

                    <div className="text-xs uppercase tracking-widest text-white/40">
                      Live Delivery
                    </div>

                    <div className="font-heading text-xl">
                      Waiting for GPS
                    </div>

                  </div>

                </div>

                <p className="text-sm text-white/50 mt-3">
                  Your delivery partner has started the delivery.
                  The live map will appear as soon as GPS location
                  is received.
                </p>

              </div>
            )}

          {/* ===================================================
              QUOTATION
          =================================================== */}

          {quotationAvailable ? (

            <div className="rounded-3xl bg-white/5 border border-[#B8E14A]/30 p-5 md:p-6 mt-4">

              {/* QUOTATION HEADER */}

              <div className="flex items-center gap-3 mb-6">

                <div className="w-11 h-11 rounded-2xl bg-[#B8E14A]/15 flex items-center justify-center">

                  <FileText className="w-5 h-5 text-[#B8E14A]" />

                </div>

                <div>

                  <h2 className="font-heading text-2xl">
                    Your Catering Quotation
                  </h2>

                  <p className="text-sm text-white/40 mt-1">
                    Quotation sent by Ice Cream Paradise
                  </p>

                </div>

              </div>

              {/* AMOUNT BREAKDOWN */}

              <div className="space-y-3">

                <div className="flex justify-between items-center rounded-2xl bg-white/5 px-4 py-3">

                  <span className="text-white/60">
                    Ice Cream
                  </span>

                  <span className="font-medium">
                    ₹
                    {Number(
                      quotation.ice_cream_amount || 0
                    ).toLocaleString("en-IN")}
                  </span>

                </div>

                <div className="flex justify-between items-center rounded-2xl bg-white/5 px-4 py-3">

                  <span className="text-white/60">
                    Staff
                  </span>

                  <span className="font-medium">
                    ₹
                    {Number(
                      quotation.staff_amount || 0
                    ).toLocaleString("en-IN")}
                  </span>

                </div>

                <div className="flex justify-between items-center rounded-2xl bg-white/5 px-4 py-3">

                  <span className="text-white/60">
                    Transport
                  </span>

                  <span className="font-medium">
                    ₹
                    {Number(
                      quotation.transport_amount || 0
                    ).toLocaleString("en-IN")}
                  </span>

                </div>

                <div className="flex justify-between items-center rounded-2xl bg-white/5 px-4 py-3">

                  <span className="text-white/60">
                    Other
                  </span>

                  <span className="font-medium">
                    ₹
                    {Number(
                      quotation.other_amount || 0
                    ).toLocaleString("en-IN")}
                  </span>

                </div>

              </div>

              {/* TOTAL */}

              <div className="border-t border-white/10 mt-5 pt-5">

                <div className="flex justify-between items-center">

                  <span className="text-white/60">
                    Total Amount
                  </span>

                  <strong className="text-2xl text-[#B8E14A]">
                    ₹
                    {totalAmount.toLocaleString(
                      "en-IN"
                    )}
                  </strong>

                </div>

              </div>

              {/* ADVANCE + BALANCE */}

              <div className="grid sm:grid-cols-2 gap-4 mt-5">

                <div className="rounded-2xl bg-white/5 p-4">

                  <span className="text-white/50 block text-sm">
                    Advance Required
                  </span>

                  <strong className="text-xl block mt-1">
                    ₹
                    {advanceAmount.toLocaleString(
                      "en-IN"
                    )}
                  </strong>

                </div>

                <div className="rounded-2xl bg-white/5 p-4">

                  <span className="text-white/50 block text-sm">
                    Balance
                  </span>

                  <strong className="text-xl block mt-1">
                    ₹
                    {balanceAmount.toLocaleString(
                      "en-IN"
                    )}
                  </strong>

                </div>

              </div>

              {/* NOTES */}

              {quotation.notes && (
                <div className="mt-5 rounded-2xl bg-white/5 p-4">

                  <span className="text-white/50 block text-sm mb-2">
                    Quotation Notes
                  </span>

                  <p className="text-white/80 whitespace-pre-line">
                    {quotation.notes}
                  </p>

                </div>
              )}

              {/* PAYMENT STATUS */}

              <div className="mt-5 rounded-2xl border border-white/10 p-4">

                <div className="flex justify-between items-center">

                  <span className="text-white/50">
                    Payment Status
                  </span>

                  <span
                    className={
                      data.payment_status ===
                      "verified"
                        ? "text-[#B8E14A] font-medium"
                        : "text-yellow-300 font-medium"
                    }
                  >
                    {data.payment_status ===
                    "verified"
                      ? "Advance Paid"
                      : "Advance Pending"}
                  </span>

                </div>

              </div>

              {/* OPEN FULL QUOTATION */}

              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/catering/quotation/${enquiryId}`
                  )
                }
                className="btn-primary w-full justify-center py-4 mt-5"
              >
                <FileText className="w-4 h-4" />
                View Full Quotation
              </button>

            </div>

          ) : (

            /* =================================================
               QUOTATION NOT AVAILABLE
            ================================================= */

            <div className="rounded-3xl bg-white/5 border border-white/10 p-5 mt-4">

              <div className="flex items-center gap-3">

                <div className="w-10 h-10 rounded-2xl bg-white/5 flex items-center justify-center">

                  <FileText className="w-5 h-5 text-white/50" />

                </div>

                <div>

                  <h2 className="font-heading text-xl">
                    Catering Quotation
                  </h2>

                  <p className="text-sm text-white/40 mt-1">
                    Your quotation will appear here
                    once our admin team sends it.
                  </p>

                </div>

              </div>

              <div className="mt-5 text-sm text-white/50">

                {data.status === "quotation_sent"
                  ? "Quotation is being loaded. Please refresh."
                  : "Quotation Pending"}

              </div>

            </div>
          )}

          {/* ===================================================
              FOOTER
          =================================================== */}

          <div className="text-center mt-8 text-sm text-white/40">

            {eventCompleted
              ? "Your catering preparation is completed."
              : "This page updates automatically every 5 seconds."}

          </div>

        </div>

      </section>

    </div>
  );
}